import { useCallback, useMemo } from "react";

import { IPetItemResponse } from "../services/pet-list/pet-list.interface";
import { MIXPANEL_EVENTS } from "../config/mixPanelEvents";
import { MODALS } from "../config/params";
import { PAGES_PATH } from "../navigation/params";
import { isWellnessMember } from "../utils/wellness";
import { useModal } from "../context/ModalContext";
import { useNavigate } from "react-router-dom";
import { useOverdueInvoiceStore } from "./redux-hooks/overdue-invoice-store/overdue-invoice.store";
import { usePetStore } from "./redux-hooks/pet-store/pet-store.service";
import { userActivityLogger } from "../utils/mixpanel";

export interface UseWellnessFunnelOptions {
  source: string;
  filterPetIds?: string[];
}

export interface UseWellnessFunnelResult {
  eligiblePets: IPetItemResponse[];
  canEnter: boolean;
  start: () => void;
  selectPet: (pet: IPetItemResponse) => void;
  blockedByDelinquency: () => boolean;
}

const isEligibleSpecies = (pet: IPetItemResponse): boolean =>
  pet.speciesName === "Dog" || pet.speciesName === "Cat";

export const isValidBirthdate = (
  pet: Pick<IPetItemResponse, "birthdate"> | undefined | null,
): boolean => {
  if (!pet?.birthdate || pet.birthdate.length === 0) return false;
  const seconds = parseInt(pet.birthdate, 10);
  if (!Number.isFinite(seconds) || seconds <= 0) return false;
  return new Date(seconds * 1000).getFullYear() >= 2000;
};

/**
 * Wellness plan purchase-funnel orchestrator. Consolidates the previously
 * duplicated CTA handlers in Dashboard, EnrollPlanCard,
 * WellnessDetailsPlanCard, and WellnessPlanContainer.
 *
 * Multi-pet picker is still rendered via the existing modal registry
 * (MODALS.WELLNESS_PLAN_SELECT_PET); this hook does not introduce a new
 * dialog component.
 *
 * @param {UseWellnessFunnelOptions} options Source (for analytics) and an
 *   optional filterPetIds list used by the renewal flow.
 * @return {UseWellnessFunnelResult} Eligible pets, a canEnter flag, and
 *   `start` / `selectPet` handlers consumers bind to their CTA.
 */
export const useWellnessFunnel = (
  options: UseWellnessFunnelOptions,
): UseWellnessFunnelResult => {
  const { source, filterPetIds } = options;
  const { petList } = usePetStore();
  const { openModal, setWellnessSelectedPetId } = useModal();
  const {
    cancelledWellnessPetList,
    pausedWellnessPetList,
    pendingPaymentPetList,
  } = useOverdueInvoiceStore();
  const navigate = useNavigate();

  const eligiblePets = useMemo<IPetItemResponse[]>(() => {
    if (!petList) return [];
    const isRenewalScope = filterPetIds && filterPetIds.length > 0;
    return petList
      .filter(isEligibleSpecies)
      .filter((pet) => {
        if (isRenewalScope) return filterPetIds!.includes(pet.id);
        return !pet.isWellnessplan;
      });
  }, [petList, filterPetIds]);

  // Per SGPD-400: "User has no pets → Do not show entry points, User cannot
  // enter the flow." and "All pets have active plans → Do not show banner".
  // canEnter is strict — only users with at least one eligible (dog/cat)
  // unenrolled pet can enter the funnel.
  const canEnter = eligiblePets.length > 0;

  const continueWithPet = useCallback(
    (pet: IPetItemResponse) => {
      setWellnessSelectedPetId(pet.id);
      const list = petList ?? [];
      if (isWellnessMember(list, pet.id, true)) {
        navigate(`${PAGES_PATH.PET_CARE}?id=${pet.id}&category=TotalCare_Plan`);
        return;
      }
      if (!isValidBirthdate(pet)) {
        openModal(MODALS.PET_BIRTHDAY_FORM_MODAL, source);
        return;
      }
      navigate(`${PAGES_PATH.WELLNESS_PLAN_DETAILS}?id=${pet.id}`);
    },
    [navigate, openModal, petList, setWellnessSelectedPetId, source],
  );

  /**
   * Blocks the funnel when the user has any pet in a delinquent state
   * (CANCELLED / PAUSED / LOCKED with pending invoices). Opens the
   * appropriate pending-invoice alert modal instead of letting the user
   * start a new WP purchase while another plan is unpaid.
   *
   * `pausedWellnessPetList` includes pets with `wellnessPlanPaymentStatus`
   * of either PAUSED or LOCKED, so the same gate covers all three
   * delinquent states.
   *
   * @return {boolean} `true` if the funnel was blocked and the caller
   *   should drop out; `false` to proceed.
   */
  const blockedByDelinquency = useCallback((): boolean => {
    if (cancelledWellnessPetList.length > 0) {
      userActivityLogger(MIXPANEL_EVENTS.DELINQUENCY_BOOKING_BLOCKED, {
        delinquency_state: "cancelled",
        entry_point: source,
        num_affected_pets: pendingPaymentPetList.length,
      });
      openModal(MODALS.PENDING_INVOICE_CANCELLED_ALERT_MODAL);
      return true;
    }
    if (pausedWellnessPetList.length > 0) {
      userActivityLogger(MIXPANEL_EVENTS.DELINQUENCY_BOOKING_BLOCKED, {
        delinquency_state: "paused",
        entry_point: source,
        num_affected_pets: pendingPaymentPetList.length,
      });
      openModal(MODALS.PENDING_INVOICE_PAUSED_ALERT_MODAL, PAGES_PATH.PET_CARE);
      return true;
    }
    return false;
  }, [
    cancelledWellnessPetList,
    openModal,
    pausedWellnessPetList,
    pendingPaymentPetList,
    source,
  ]);

  const start = useCallback(() => {
    userActivityLogger(MIXPANEL_EVENTS.INTEREST_IN_WELLNESS_PLAN, { referred_from: source });

    if (blockedByDelinquency()) return;

    if (!petList || petList.length === 0) {
      openModal(MODALS.ADD_A_PET, source);
      return;
    }

    if (eligiblePets.length === 0) {
      return;
    }

    if (eligiblePets.length === 1) {
      continueWithPet(eligiblePets[0]);
      return;
    }

    openModal(
      MODALS.WELLNESS_PLAN_SELECT_PET,
      source,
      filterPetIds && filterPetIds.length > 0 ? { petIds: filterPetIds } : undefined,
    );
  }, [
    blockedByDelinquency,
    continueWithPet,
    eligiblePets,
    filterPetIds,
    openModal,
    petList,
    source,
  ]);

  const selectPet = useCallback(
    (pet: IPetItemResponse) => {
      userActivityLogger(MIXPANEL_EVENTS.INTEREST_IN_WELLNESS_PLAN, { referred_from: source });
      if (blockedByDelinquency()) return;
      continueWithPet(pet);
    },
    [blockedByDelinquency, continueWithPet, source],
  );

  return {
    eligiblePets,
    canEnter,
    start,
    selectPet,
    blockedByDelinquency,
  };
};
